import { Center, Stack, Text, Button, Group } from '@mantine/core'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { IconConfetti, IconArrowLeft, IconRefresh, IconChartRadar, IconTrendingUp, IconUsers } from '@tabler/icons-react'
import { useAuth } from '../../hooks/useAuth'
import { UpgradeForm } from '../common/UpgradeForm'
import { getProfileConfig } from '../../data/projected-profiles'

const EXPLORE_BASE_URL = 'https://bubotexplore.vercel.app'

export function StoryCompleteScreen({ story, serverSessionId, onPlayAgain }) {
  const navigate = useNavigate()
  const { isProvisional } = useAuth()
  // This screen renders inside GamePlayer while still on /play/:id?team=&ts=, so
  // team identity is in the URL. A team flow gets a "Back to Team" return; solo
  // goes back to the story list. Nothing persisted to clear.
  const [searchParams] = useSearchParams()
  const teamId = searchParams.get('team')
  const inTeamFlow = !!teamId

  const goBackToTeam = () => navigate(`/teams/${teamId}`)

  const goBackHome = () => navigate('/')

  return (
    <Center style={{ height: '100%', overflow: 'auto' }}>
      <Stack align="center" gap="lg" maw={400} px="md">
        <IconConfetti
          size={64}
          color="var(--mantine-color-cyan-4)"
          style={{ filter: 'drop-shadow(0 0 10px rgba(0, 229, 255, 0.4))' }}
        />

        <Text
          size="2rem"
          fw={700}
          ff="Orbitron, sans-serif"
          c="cyan.3"
          ta="center"
          className="glow-text-cyan"
        >
          Mission Complete
        </Text>

        <Text c="dimmed" ta="center" size="lg">
          You've completed all chapters in{' '}
          <Text span c="cyan.4" fw={600}>
            {story?.name || 'this story'}
          </Text>
          . Your responses have been saved.
        </Text>

        {isProvisional && (
          <UpgradeForm
            title="Create an Account to Receive Your Analysis"
            description="Set your name and password to unlock your personalized analysis."
            successMessage="Account created! Your analysis will be available soon."
          />
        )}

        <Group gap="md" mt="md">
          {inTeamFlow ? (
            <Button
              variant="light"
              color="cyan"
              leftSection={<IconUsers size={16} />}
              onClick={goBackToTeam}
            >
              Back to Team
            </Button>
          ) : (
            <Button
              variant="light"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={goBackHome}
            >
              Back to Stories
            </Button>
          )}
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={onPlayAgain}
          >
            Play Again
          </Button>
        </Group>

        {!isProvisional && serverSessionId && (
          <Button
            variant="filled"
            color="cyan"
            size="md"
            leftSection={<IconChartRadar size={18} />}
            onClick={() => navigate(`/analysis/${serverSessionId}`)}
            className="glow-cyan"
            fullWidth
          >
            View Your Session Debrief
          </Button>
        )}

        {!isProvisional && serverSessionId && getProfileConfig(story?.id) && (
          <Button
            variant="light"
            color="cyan"
            size="md"
            leftSection={<IconChartRadar size={18} />}
            onClick={() => navigate(`/profile/projected/${serverSessionId}`)}
            fullWidth
          >
            See What 24 Weeks Looks Like
          </Button>
        )}

        {!isProvisional && serverSessionId && (
          <Button
            component="a"
            href={`${EXPLORE_BASE_URL}/?session=${serverSessionId}&token=${(() => { try { return JSON.parse(localStorage.getItem('bw_authToken') || '') } catch { return '' } })()}`}
            target="_blank"
            variant="filled"
            color="lime"
            size="md"
            leftSection={<IconTrendingUp size={18} />}
            fullWidth
          >
            Explore Your Growth Path
          </Button>
        )}
      </Stack>
    </Center>
  )
}
